import type { DeckDetails } from "@amzn/ads-magneto-typescript-client";
import { Button, Tooltip } from "@amzn/storm-ui";
import React from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { ACCOUNT_AGNOSTIC_UNAVAILABLE } from "../../../config/constants";
import { TELEMETRY_ID } from "../../../constants/telemetry";
import { csatFeedbackStore } from "../../../store/csatFeedback";
import { editContentStore } from "../../../store/editContent";
import { globalWorkbenchStore } from "../../../store/globalWorkbench";
import { navigationStore } from "../../../store/navigation";
import { salesforceStore } from "../../../store/salesforce";
import { yourInsightFilterStore } from "../../../store/yourInsightsFilter";
import {
    YourInsightFilterContentType,
    YourInsightFilterContentSubType,
} from "../../../types/filters/yourInsights";
import { TopNavBarTabs } from "../../../types/navigation";
import {
    getAdvertiserId,
    getNormalizedAdvertiserId,
    isAccountAgnosticId,
} from "../../../utils/permission.utils";
import { logWithTakt } from "../../../utils/telemetry.utils";
import {
    DivFlexAlignCenter,
    DivFlexSpaceBetween,
} from "../../common/styled.components";
import { ContactUs } from "../../contactUs/ContactUs";
import { GlobalWorkbenchCartButton } from "../../remote";
import { UploadPitchedContentModal } from "../insights/pitchedContents/UploadPitchedContentModal";

import { TabbedNav } from "./TabbedNav";

const StyledTopNavBarContainer = styled(DivFlexSpaceBetween)`
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
`;

export interface TopNavBarProps {
    hasPlmAccess?: boolean;
}

export const TopNavBar: React.FunctionComponent<TopNavBarProps> = (
    props: TopNavBarProps,
) => {
    const { hasPlmAccess = false } = props;
    const advertiserId = getAdvertiserId();

    const [activeTab, setActiveTab, setIdentifier] = useStore(
        navigationStore,
        (state) => [state.activeTab, state.setActiveTab, state.setIdentifier],
    );

    const [showEditModal] = useStore(editContentStore, (state) => [
        state.showEditModal,
    ]);

    const [sfName] = useStore(salesforceStore, (state) => [
        state.sfContext.name,
    ]);

    const [setYourInsightsTypes] = useStore(yourInsightFilterStore, (state) => [
        state.setTypes,
    ]);

    const handleOnTabChange = (tab: TopNavBarTabs) => {
        if (!advertiserId && tab !== TopNavBarTabs.YOUR_INSIGHT_DECKS) {
            return;
        }

        setActiveTab(tab);
        logWithTakt(TELEMETRY_ID.TOP_NAV_BAR_SELECTION, {
            tab,
        });
    };

    const shouldDisablePlmUpload = isAccountAgnosticId();
    const [setIsEligibleToProvideFeedback] = useStore(
        csatFeedbackStore,
        (state) => [state.setHasUserPerformedActionWorthFeedback],
    );

    return (
        <StyledTopNavBarContainer>
            <TabbedNav
                activeTab={activeTab}
                handleOnTabChange={handleOnTabChange}
            />
            <DivFlexAlignCenter>
                <ContactUs />
                <GlobalWorkbenchCartButton
                    workbenchScope={getNormalizedAdvertiserId()}
                    overrideInsightsHubRedirect={(savedDeckId?: string) => {
                        setActiveTab(TopNavBarTabs.YOUR_INSIGHT_DECKS);
                        setYourInsightsTypes(
                            YourInsightFilterContentType.INSIGHTS,
                            YourInsightFilterContentSubType.WORKBENCH_INSIGHTS,
                        );
                        setIdentifier(savedDeckId);

                        // 2 seconds after the redirect, mark them eligible for csat feedback
                        setTimeout(() => {
                            setIsEligibleToProvideFeedback(true);
                        }, 2000);
                    }}
                    title={sfName && `${sfName} Insights Workbench`}
                    onCartDataLoaded={(cartData: DeckDetails) => {
                        globalWorkbenchStore.getState().setCartData(cartData);
                    }}
                />
                {activeTab === TopNavBarTabs.YOUR_INSIGHT_DECKS &&
                    hasPlmAccess && (
                        <Tooltip
                            nonInteractive={!shouldDisablePlmUpload}
                            inert={!shouldDisablePlmUpload}
                            message={ACCOUNT_AGNOSTIC_UNAVAILABLE}
                            trigger={
                                <span>
                                    <Button
                                        onClick={() => {
                                            showEditModal();
                                        }}
                                        data-testid="top-nav-bar-upload-insight-deck"
                                        disabled={shouldDisablePlmUpload}
                                    >
                                        Upload insight deck
                                    </Button>
                                </span>
                            }
                        />
                    )}
            </DivFlexAlignCenter>
            <UploadPitchedContentModal />
        </StyledTopNavBarContainer>
    );
};
